import { Button, HStack, MenuItem } from "@chakra-ui/react";
import { useClosingRetrieve } from "gen/endpoints/closing/closing";
import { useCommitmentInformationTableDataRetrieveSuspense } from "gen/endpoints/commitment-information-table-data/commitment-information-table-data";
import { useFundFamilyEventRetrieve } from "gen/endpoints/fund-family-event/fund-family-event";
import { CommitmentInformationTableDataRetrieveParams } from "gen/model/commitmentInformationTableDataRetrieveParams";
import { FundFamilyEventStateMachineFieldsEnum } from "gen/model/fundFamilyEventStateMachineFieldsEnum";
import { PermissionTypeEnum } from "gen/model/permissionTypeEnum";
import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Restricted } from "shared/components/access/Restricted";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import { defaultDataTableRenderCellValue } from "shared/components/table/data/DataTableCellValueRenderer";
import {
  GetLinkInfoForCellFn,
  GetLinkInfoForRowFn,
  RenderCellValueFn,
  RenderRowActionsFn,
  StylesForCellFn,
} from "shared/components/table/data/DataTableTypes";
import { rootRoute } from "shared/navigation/Routes";

export interface CommitmentsTableProps {
  fundFamilyId: string;
  params?: CommitmentInformationTableDataRetrieveParams;
  onClickAddCommitment?: () => void;
  onClickBulkAddCommitments?: () => void;
  onClickRowMenuItem?: (commitmentId: string) => void;
  onClickDeleteCommitment?: (closingId: string) => void;
  isFullWidth?: boolean;
  isSetup?: boolean;
  tableTitle?: string;
  hideIfEmpty?: boolean;
}

export const CommitmentsTable: React.FC<CommitmentsTableProps> = ({
  fundFamilyId,
  params,
  onClickAddCommitment,
  onClickBulkAddCommitments,
  onClickRowMenuItem,
  onClickDeleteCommitment,
  isFullWidth,
  tableTitle,
  isSetup,
  hideIfEmpty,
}) => {
  const { t } = useTranslation(["commitment", "common", "closing"]);

  const { data: tableData } = useCommitmentInformationTableDataRetrieveSuspense(
    {
      ...params,
      fund_family_id: fundFamilyId,
    },
  );

  const closingId = params?.closing_id;
  const { data: closingData } = useClosingRetrieve(fundFamilyId, closingId!, {
    query: { enabled: !!closingId },
  });
  const fundFamilyEventId = closingData?.fund_family_event_id;
  const { data: fundFamilyEventData } = useFundFamilyEventRetrieve(
    fundFamilyId,
    fundFamilyEventId!,
    {
      query: {
        enabled: !!fundFamilyEventId,
      },
    },
  );
  const isDraft =
    fundFamilyEventData?.state === FundFamilyEventStateMachineFieldsEnum.draft;
  const canAddCommitment =
    ((closingId && isDraft) ||
      (!closingId && params?.is_unattached_to_closing)) &&
    (onClickAddCommitment || onClickBulkAddCommitments);

  const getLinkInfoForRow = useCallback<GetLinkInfoForRowFn>(
    (props) => {
      if (props.row.data?.metadata?.type !== "commitment") {
        return undefined;
      }
      const { commitment } = props.row.data.metadata;
      if (isSetup) {
        return {
          type: "generic",
          to: rootRoute({})
            .fundFamilies({})
            .fundFamily({ fundFamilyId })
            .setup({})
            .commitments({})
            .commitment({ commitmentId: commitment.id }).$,
        };
      }
      return undefined;
    },
    [fundFamilyId, isSetup],
  );

  const getLinkInfoForCell = useCallback<GetLinkInfoForCellFn>(
    (props) => {
      if (
        isSetup ||
        props.type !== "data" ||
        props.row.data?.metadata?.type !== "commitment"
      ) {
        return undefined;
      }
      const { commitment } = props.row.data.metadata;
      switch (props.columnContext.columnId) {
        case "owning_entity_name":
          return {
            type: "generic",
            to: rootRoute({})
              .fundFamilies({})
              .fundFamily({ fundFamilyId })
              .commitments({})
              .investorEntities({})
              .investorEntity({
                investorEntityId: commitment.owning_entity_id,
              })
              .tabActivity({}).$,
          };
        case "view_commitment":
          return {
            type: "generic",
            to: rootRoute({})
              .fundFamilies({})
              .fundFamily({ fundFamilyId })
              .commitments({})
              .commitment({ commitmentId: commitment.id })
              .activity({}).$,
          };
      }

      return undefined;
    },
    [fundFamilyId, isSetup],
  );

  const stylesForCell = useCallback<StylesForCellFn>(
    ({ columnContext: { column } }) => {
      switch (column.id) {
        case "custom_fee":
        case "custom_waterfall":
        case "side_letter_id":
          return {
            textAlign: "center",
            width: "120px",
          };
      }
    },
    [],
  );

  const renderCellValue = useCallback<RenderCellValueFn>(
    (props, fieldDisabled, params) => {
      const { columnContext, row } = props;
      if (columnContext.columnId === "view_commitment" && !row.data) {
        return true;
      }
      return defaultDataTableRenderCellValue(props, fieldDisabled, params);
    },
    [],
  );

  const renderRowActions = useMemo(
    (): RenderRowActionsFn | undefined =>
      onClickRowMenuItem
        ? ({ row }) => ({
            type: "menu",
            actions:
              row.data?.metadata?.type === "commitment" ? (
                <>
                  <MenuItem onClick={() => onClickRowMenuItem(row.id!)}>
                    {params?.is_unattached_to_closing
                      ? t("closing:addToClosing")
                      : t("closing:removeFromClosing")}
                  </MenuItem>
                  {onClickDeleteCommitment && (
                    <MenuItem onClick={() => onClickDeleteCommitment(row.id!)}>
                      {t("commitment:delete.title")}
                    </MenuItem>
                  )}
                </>
              ) : undefined,
          })
        : undefined,
    [
      onClickDeleteCommitment,
      onClickRowMenuItem,
      params?.is_unattached_to_closing,
      t,
    ],
  );

  if (hideIfEmpty && !tableData.rows.length) {
    return null;
  }

  return (
    <TableBox isFullWidth={isFullWidth}>
      <TableBoxHeader>
        <TableBoxTitle>{tableTitle || t("common:commitments")}</TableBoxTitle>
        <DataTableBoxActions
          createAction={
            canAddCommitment ? (
              <Restricted permission={PermissionTypeEnum.write}>
                {onClickAddCommitment && (
                  <Button
                    size="xs"
                    colorScheme="secondary"
                    onClick={onClickAddCommitment}
                  >
                    {t("commitment:add.title")}
                  </Button>
                )}
                {onClickBulkAddCommitments && (
                  <Button
                    size="xs"
                    colorScheme="secondary"
                    onClick={onClickBulkAddCommitments}
                  >
                    {t("commitment:bulkAdd.cta")}
                  </Button>
                )}
              </Restricted>
            ) : null
          }
        />
      </TableBoxHeader>
      <TableBoxBody>
        <DataTable
          data={tableData}
          emptyStateProps={{
            title: t("commitment:empty.title"),
            description: t("commitment:empty.description"),
            children: canAddCommitment ? (
              <Restricted permission={PermissionTypeEnum.write}>
                <HStack spacing="24px" justifyContent="center">
                  {onClickAddCommitment && (
                    <Button size="sm" onClick={onClickAddCommitment}>
                      {t("commitment:add.title")}
                    </Button>
                  )}
                  {onClickBulkAddCommitments && (
                    <Button size="sm" onClick={onClickBulkAddCommitments}>
                      {t("commitment:bulkAdd.cta")}
                    </Button>
                  )}
                </HStack>
              </Restricted>
            ) : null,
          }}
          getLinkInfoForRow={getLinkInfoForRow}
          getLinkInfoForCell={getLinkInfoForCell}
          stylesForCell={stylesForCell}
          renderCellValue={renderCellValue}
          renderRowActions={renderRowActions}
        />
      </TableBoxBody>
    </TableBox>
  );
};
